import React from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";
import { AuthProvider, can, useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Bookings from "@/pages/Bookings";
import Quotations from "@/pages/Quotations";
import { CustomerDetail, Customers } from "@/pages/Customers";
import { AgentDetail, Agents } from "@/pages/Agents";
import Services from "@/pages/Services";
import Invoices from "@/pages/Invoices";
import InvoiceCreate from "@/pages/InvoiceCreate";
import InvoiceDetail from "@/pages/InvoiceDetail";
import Payments from "@/pages/Payments";
import Expenses from "@/pages/Expenses";
import Receivables from "@/pages/Receivables";
import Payables from "@/pages/Payables";
import Ledgers from "@/pages/Ledgers";
import GstCenter from "@/pages/GstCenter";
import Reports from "@/pages/Reports";
import Settings from "@/pages/Settings";
import Users from "@/pages/Users";
import AuditLog from "@/pages/AuditLog";

function Loading() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#092B28]">
      <div className="text-center">
        <div className="font-display text-2xl font-bold text-white tracking-wide">DREAMZZ TRAVELS</div>
        <div className="text-xs uppercase tracking-[0.25em] text-[#C9A227] mt-1">Loading workspace…</div>
      </div>
    </div>
  );
}

function Guard({ module, children }) {
  const { user } = useAuth();
  if (user === undefined) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (module && !can(user.role, module)) {
    return (
      <div className="py-24 text-center" data-testid="access-denied">
        <h2 className="font-display text-2xl font-bold text-[#1A2E2C]">Access restricted</h2>
        <p className="text-sm text-[#5C6B69] mt-2">Your role ({user.role}) does not have access to this module.</p>
      </div>
    );
  }
  return children;
}

function AppRoutes() {
  const { user } = useAuth();
  if (user === undefined) return <Loading />;
  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/" replace /> : <Login />} />
      <Route element={<Guard><Layout /></Guard>}>
        <Route path="/" element={<Dashboard />} />
        <Route path="/bookings" element={<Guard module="bookings"><Bookings /></Guard>} />
        <Route path="/quotations" element={<Guard module="quotations"><Quotations /></Guard>} />
        <Route path="/customers" element={<Guard module="customers"><Customers /></Guard>} />
        <Route path="/customers/:id" element={<Guard module="customers"><CustomerDetail /></Guard>} />
        <Route path="/agents" element={<Guard module="agents"><Agents /></Guard>} />
        <Route path="/agents/:id" element={<Guard module="agents"><AgentDetail /></Guard>} />
        <Route path="/services" element={<Guard module="services"><Services /></Guard>} />
        <Route path="/invoices" element={<Guard module="invoices"><Invoices /></Guard>} />
        <Route path="/invoices/new" element={<Guard module="invoices"><InvoiceCreate /></Guard>} />
        <Route path="/invoices/:id" element={<Guard module="invoices"><InvoiceDetail /></Guard>} />
        <Route path="/credit-notes" element={<Guard module="notes"><Invoices fixedDocType="credit_note" title="Credit Notes" subtitle="GST credit notes with original invoice references" /></Guard>} />
        <Route path="/debit-notes" element={<Guard module="notes"><Invoices fixedDocType="debit_note" title="Debit Notes" subtitle="GST debit notes with original invoice references" /></Guard>} />
        <Route path="/payments" element={<Guard module="payments"><Payments /></Guard>} />
        <Route path="/expenses" element={<Guard module="expenses"><Expenses /></Guard>} />
        <Route path="/receivables" element={<Guard module="reports"><Receivables /></Guard>} />
        <Route path="/payables" element={<Guard module="reports"><Payables /></Guard>} />
        <Route path="/ledgers" element={<Guard module="ledgers"><Ledgers /></Guard>} />
        <Route path="/gst" element={<Guard module="gst"><GstCenter /></Guard>} />
        <Route path="/reports" element={<Guard module="reports"><Reports /></Guard>} />
        <Route path="/settings" element={<Guard module="settings"><Settings /></Guard>} />
        <Route path="/users" element={<Guard module="users"><Users /></Guard>} />
        <Route path="/audit" element={<Guard module="audit"><AuditLog /></Guard>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster position="top-right" richColors closeButton />
      </BrowserRouter>
    </AuthProvider>
  );
}
